import {
  getFromLocalStorage,
  removeFromLocalStorage,
  setLocalStorage,
} from '@/local-storage/utils';

/** A language the app ships translations for. */
export type AppLanguage = 'en' | 'nl';

/** The code the backend stores a client's language under. */
export type BackendLanguageCode = 'en-US' | 'nl-NL';

/**
 * How long a pick made before signing in stays pending. Signing in normally follows within
 * minutes; an older pick is more likely someone else's on a shared browser, and the language
 * endpoint it is sent to overrides even a psychologist's choice.
 */
export const PENDING_LANGUAGE_CHOICE_TTL_MS = 60 * 60 * 1000;

type PendingLanguageChoice = {
  language: AppLanguage;
  chosenAt: number;
};

export const toBackendLanguageCode = (language: AppLanguage): BackendLanguageCode =>
  language === 'en' ? 'en-US' : 'nl-NL';

/**
 * Remembers a language picked where the choice cannot be sent right away — the landing page has no
 * session yet — so it can be sent once the person is signed in.
 */
export const markLanguageChoicePending = (language: AppLanguage, now: number = Date.now()) =>
  setLocalStorage(
    'pending-language-choice',
    JSON.stringify({ language, chosenAt: now } satisfies PendingLanguageChoice)
  );

/** Forgets a pending choice, eg. because a newer one was just sent from the settings. */
export const clearPendingLanguageChoice = () => removeFromLocalStorage('pending-language-choice');

const parsePendingLanguageChoice = (stored: string): PendingLanguageChoice | null => {
  try {
    const { language, chosenAt } = JSON.parse(stored) as Partial<PendingLanguageChoice>;

    return (language === 'en' || language === 'nl') && typeof chosenAt === 'number'
      ? { language, chosenAt }
      : null;
  } catch {
    return null;
  }
};

/**
 * Returns the pending choice as a backend code and forgets it, so it is sent at most once. A pick
 * older than {@link PENDING_LANGUAGE_CHOICE_TTL_MS} is dropped instead.
 *
 * The landing page's language used to reach the backend in the report sent on every page load,
 * which could not tell a choice apart from the Dutch it fell back to and flipped English clients
 * back to Dutch (IP-2221). Sending it once, explicitly, keeps the choice without that report.
 */
export const takePendingLanguageChoice = (now: number = Date.now()): BackendLanguageCode | null => {
  const stored = getFromLocalStorage('pending-language-choice');

  if (stored == null) {
    return null;
  }

  clearPendingLanguageChoice();

  const pending = parsePendingLanguageChoice(stored);

  if (pending == null || now - pending.chosenAt > PENDING_LANGUAGE_CHOICE_TTL_MS) {
    return null;
  }

  return toBackendLanguageCode(pending.language);
};
